import Image from '@/components/Image';
import { mediaBreakpointUp } from '@/styles/layout/utils';
import { remCalc } from '@/styles/typography/utils';
import { FC } from 'react';
import styled from 'styled-components';

const IndustryIllustration = styled.div`
  display: block;
  margin: 0 auto;
  z-index: -1;

  img {
    width: 100%;
  }

  ${mediaBreakpointUp('tablet')} {
    position: absolute;
  }
`;

const B2BTechIllustration = styled(IndustryIllustration)`
  width: ${remCalc(302)};

  ${mediaBreakpointUp('tablet')} {
    position: absolute;
    top: ${remCalc(60)};
    left: ${remCalc(-40)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    top: 0;
    width: ${remCalc(499)};
  }
`;

const FinServIllustration = styled(IndustryIllustration)`
  width: ${remCalc(302)};

  ${mediaBreakpointUp('tablet')} {
    top: ${remCalc(60)};
    left: ${remCalc(-40)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    top: 0;
    width: ${remCalc(499)};
  }
`;

const LogisticsIllustration = styled(IndustryIllustration)`
  width: ${remCalc(302)};

  ${mediaBreakpointUp('tablet')} {
    top: ${remCalc(60)};
    left: ${remCalc(-40)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    top: 0;
    width: ${remCalc(499)};
  }
`;

const ManufacturingIllustration = styled(IndustryIllustration)`
  width: ${remCalc(302)};

  ${mediaBreakpointUp('tablet')} {
    top: ${remCalc(60)};
    left: ${remCalc(-20)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    top: 0;
    width: ${remCalc(499)};
  }
`;

const ProfessionalServicesIllustration = styled(IndustryIllustration)`
  width: ${remCalc(302)};

  ${mediaBreakpointUp('tablet')} {
    top: ${remCalc(60)};
    left: ${remCalc(-40)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    top: 0;
    width: ${remCalc(499)};
  }
`;

const TravelIllustration = styled(IndustryIllustration)`
  width: ${remCalc(302)};

  ${mediaBreakpointUp('tablet')} {
    top: ${remCalc(60)};
    left: ${remCalc(-40)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    top: 0;
    width: ${remCalc(499)};
  }
`;

const TeamsIllustration = styled.div`
  position: absolute;
  display: none;

  img {
    width: 100%;
  }

  ${mediaBreakpointUp('tablet')} {
    display: block;
  }
`;

const SupportIllustration = styled(TeamsIllustration)`
  top: ${remCalc(110)};
  left: ${remCalc(35)};
  width: ${remCalc(204)};

  ${mediaBreakpointUp('desktopWide')} {
    top: ${remCalc(72)};
    width: ${remCalc(453)};
  }
`;

const SuccessIllustration = styled(TeamsIllustration)`
  top: ${remCalc(50)};
  left: 0;
  width: ${remCalc(239)};

  ${mediaBreakpointUp('desktopWide')} {
    top: ${remCalc(85)};
    width: ${remCalc(431)};
  }
`;

const FinanceIllustration = styled(TeamsIllustration)`
  top: ${remCalc(66)};
  left: ${remCalc(35)};
  width: ${remCalc(153)};

  ${mediaBreakpointUp('desktopWide')} {
    top: ${remCalc(72)};
    width: ${remCalc(245)};
  }
`;

const AccountManagementIllustration = styled(TeamsIllustration)`
  top: ${remCalc(100)};
  left: ${remCalc(25)};
  width: ${remCalc(200)};

  ${mediaBreakpointUp('desktopWide')} {
    top: calc(100% - ${remCalc(412)});
    left: ${remCalc(35)};
    width: ${remCalc(344)};
  }
`;

const InboundSalesIllustration = styled(TeamsIllustration)`
  top: ${remCalc(40)};
  left: ${remCalc(35)};
  width: ${remCalc(153)};

  ${mediaBreakpointUp('desktopWide')} {
    top: calc(100% - ${remCalc(422)});
    left: ${remCalc(35)};
    width: ${remCalc(238)};
  }
`;

const OperationsIllustration = styled(TeamsIllustration)`
  top: ${remCalc(66)};
  left: ${remCalc(35)};
  width: ${remCalc(200)};

  ${mediaBreakpointUp('desktopWide')} {
    top: calc(100% - ${remCalc(359)});
    left: ${remCalc(35)};
    width: ${remCalc(308)};
  }
`;

interface HeroIllustrationProps {
  name: string | null | undefined;
}

const HeroIllustration: FC<HeroIllustrationProps> = ({ name }) => {
  switch (name) {
    case 'b2bTech':
      return (
        <B2BTechIllustration>
          <Image src="/webassets/illustrations/industry-b2b-tech.svg" width={499} height={745} alt="" />
        </B2BTechIllustration>
      );

    case 'financialServices':
      return (
        <FinServIllustration>
          <Image src="/webassets/illustrations/industry-finserv.svg" width={499} height={745} alt="" />
        </FinServIllustration>
      );

    case 'logistics':
      return (
        <LogisticsIllustration>
          <Image src="/webassets/illustrations/industry-logistics.svg" width={303} height={452} alt="" />
        </LogisticsIllustration>
      );

    case 'manufacturing':
      return (
        <ManufacturingIllustration>
          <Image src="/webassets/illustrations/industry-manufacturing.svg" width={499} height={745} alt="" />
        </ManufacturingIllustration>
      );

    case 'professionalServices':
      return (
        <ProfessionalServicesIllustration>
          <Image src="/webassets/illustrations/industry-profserv.svg" width={499} height={745} alt="" />
        </ProfessionalServicesIllustration>
      );

    case 'travel':
      return (
        <TravelIllustration>
          <Image src="/webassets/illustrations/industry-travel.svg" width={499} height={745} alt="" />
        </TravelIllustration>
      );

    case 'support':
      return (
        <SupportIllustration>
          <Image src="/webassets/illustrations/woman-sitting-in-chair.svg" width={206} height={312} alt="" />
        </SupportIllustration>
      );

    case 'success':
      return (
        <SuccessIllustration>
          <Image src="/webassets/illustrations/man-waiting-on-taxi.svg" width={240} height={338} alt="" />
        </SuccessIllustration>
      );

    case 'finance':
      return (
        <FinanceIllustration>
          <Image src="/webassets/illustrations/woman-holding-book.svg" width={154} height={384} alt="" />
        </FinanceIllustration>
      );

    case 'accountManagement':
      return (
        <AccountManagementIllustration>
          <Image src="/webassets/illustrations/team-account-management.svg" width={975} height={1707} alt="" />
        </AccountManagementIllustration>
      );

    case 'inboundSales':
      return (
        <InboundSalesIllustration>
          <Image src="/webassets/illustrations/team-inbound-sales.svg" width={551} height={1406} alt="" />
        </InboundSalesIllustration>
      );

    case 'operations':
      return (
        <OperationsIllustration>
          <Image src="/webassets/illustrations/team-operations.svg" width={619} height={1042} alt="" />
        </OperationsIllustration>
      );

    default:
      return null;
  }
};

export default HeroIllustration;
